import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Organization } from '@/services/api/clients/organization';
import { Location } from '@/services/api/clients/locations';
import { useFlowBuilderStore } from '@/app/(hub)/leads/_stores/flowBuilderStore';

interface HubStoreState {
  // State
  selectedOrganization: Organization | null;
  selectedLocation: Location | null;
  expandedOrgIds: string[];
  addingLocationToCompany: string | null;
  searchQuery: string;
  shouldScrollToSelection: boolean;

  // Actions
  setSelectedOrganization: (
    org: Organization | null,
    options?: { preserveLocation?: boolean }
  ) => void;
  setSelectedLocation: (location: Location | null) => void;
  setAddingLocationToCompany: (orgUuid: string | null) => void;
  setSearchQuery: (query: string) => void;
  setShouldScrollToSelection: (shouldScroll: boolean) => void;
  toggleOrgExpansion: (orgUuid: string) => void;
  setOrgExpansion: (orgUuid: string, expanded: boolean) => void;
  startAddingLocation: (orgUuid?: string) => void;
  startAddingOrganization: () => void;
  resetSelection: () => void;
}

export const useHubStore = create<HubStoreState>()(
  persist(
    (set) => ({
      // Initial State
      selectedOrganization: null,
      selectedLocation: null,
      expandedOrgIds: [],
      addingLocationToCompany: null,
      searchQuery: '',
      shouldScrollToSelection: false,

      // Actions
      setSelectedOrganization: (org, options) =>
        set((state) => {
          const newExpanded =
            org && !state.expandedOrgIds.includes(org.uuid)
              ? [...state.expandedOrgIds, org.uuid]
              : state.expandedOrgIds;

          const nextLocation = options?.preserveLocation ? state.selectedLocation : null;

          return {
            selectedOrganization: org,
            selectedLocation: nextLocation,
            expandedOrgIds: newExpanded,
          };
        }),

      setSelectedLocation: (location) =>
        set((state) => {
          const orgUuid = location?.organization_uuid || state.selectedOrganization?.uuid;
          const newExpanded =
            orgUuid && !state.expandedOrgIds.includes(orgUuid)
              ? [...state.expandedOrgIds, orgUuid]
              : state.expandedOrgIds;

          // Sync with flowBuilderStore
          useFlowBuilderStore
            .getState()
            .setSelectedContext(location ? `location-${location.id}` : '');

          return {
            selectedLocation: location,
            expandedOrgIds: newExpanded,
          };
        }),

      setAddingLocationToCompany: (orgUuid) => set({ addingLocationToCompany: orgUuid }),

      setSearchQuery: (query) => set({ searchQuery: query }),

      setShouldScrollToSelection: (shouldScroll) => set({ shouldScrollToSelection: shouldScroll }),

      toggleOrgExpansion: (orgUuid) =>
        set((state) => {
          const newExpanded = state.expandedOrgIds.includes(orgUuid)
            ? state.expandedOrgIds.filter((id) => id !== orgUuid)
            : [...state.expandedOrgIds, orgUuid];
          return { expandedOrgIds: newExpanded };
        }),

      setOrgExpansion: (orgUuid, expanded) =>
        set((state) => {
          const isExpanded = state.expandedOrgIds.includes(orgUuid);
          if (expanded && !isExpanded) {
            return { expandedOrgIds: [...state.expandedOrgIds, orgUuid] };
          } else if (!expanded && isExpanded) {
            return { expandedOrgIds: state.expandedOrgIds.filter((id) => id !== orgUuid) };
          }
          return {};
        }),

      startAddingLocation: (orgUuid) =>
        set((state) => ({
          selectedLocation: {
            id: '__new_location__',
            name: '__new_location__',
            organization_uuid: orgUuid || state.selectedOrganization?.uuid || '',
            service_types: [],
            url: '',
          },
          addingLocationToCompany: orgUuid || state.selectedOrganization?.uuid || null,
        })),

      startAddingOrganization: () =>
        set({
          selectedOrganization: null,
          selectedLocation: null,
        }),

      resetSelection: () => {
        useFlowBuilderStore.getState().setSelectedContext('');
        set({
          selectedOrganization: null,
          selectedLocation: null,
          expandedOrgIds: [],
          searchQuery: '',
          shouldScrollToSelection: false,
        });
      },
    }),
    {
      name: 'hub-selection-storage',
      version: 1,
      migrate: () => ({}),
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedOrganization: state.selectedOrganization,
        selectedLocation: state.selectedLocation,
        expandedOrgIds: state.expandedOrgIds,
      }),
      // When state is rehydrated (page load), trigger scroll to selection
      onRehydrateStorage: () => (state) => {
        if (state && (state.selectedOrganization || state.selectedLocation)) {
          // Defer the scroll trigger to next tick to ensure UI is ready
          setTimeout(() => {
            state.setShouldScrollToSelection(true);
          }, 100);
        }
      },
    }
  )
);
